const childrenLoaded = require('@alexspirgel/children-loaded');
import Player from '@vimeo/player';

class HeroVideo extends HTMLElement {
    constructor() {
        super();
    }

    pauseCurrentSlideVideo = (Slide) => {
        const currentSlide = Slide.slide;
        const video = currentSlide.querySelector('video');
        const iframe = currentSlide.querySelector('iframe');

        if (video) {
            video.pause();
            currentSlide.classList.remove('video-played');
        }

        if (iframe && iframe.src.includes('vimeo')) {
            new Vimeo.Player(iframe).pause();
        }

        if (iframe && iframe.src.includes('youtube')) {
            iframe.contentWindow.postMessage('{"event":"command","func":"stopVideo","args":""}', '*');
        }
    };

    isMobile() {
        return typeof Screen.orientation !== "undefined" || navigator.userAgent.indexOf('Mobi') !== -1;
    }

    setUrlParams(iframeSrc, videoId) {
        const url = new URL(iframeSrc);

        if (!this.isMobile()) {
            url.searchParams.set('autoplay', '1');
        }

        url.searchParams.set('controls', '0');
        url.searchParams.set('playsinline', '1');
        url.searchParams.set('loop', '1');

        if (iframeSrc.includes('vimeo')) {
            url.searchParams.set('title', '0');
            url.searchParams.set('byline', '0');
            url.searchParams.set('sidedock', '0');
            url.searchParams.set('muted', '1')
            url.searchParams.set('keyboard', '0')
        } else {
            url.searchParams.set('muted', '1');
            url.searchParams.set('mute', '1');
            url.searchParams.set('enablejsapi', '1');
            url.searchParams.set('modestbranding', '0');
            url.searchParams.set('rel', '0');
            url.searchParams.set('playlist', videoId);
        }
        return url.toString();
    }

    setPlayedState(playerControlContainer, isPlayed = true) {
        if (!playerControlContainer) {
            return;
        }
        playerControlContainer.setAttribute('data-is-played', isPlayed ? 'true' : 'false');
    }

    announceState(playerControlContainer, message) {
        const status = playerControlContainer ? playerControlContainer.querySelector('.home-page-hero__video-status') : null;
        if (status) {
            status.textContent = message;
        }
    }

    // The pressed button is hidden once the state flips, so hand focus to the
    // button that replaces it instead of letting it fall back to the page.
    moveFocusToVisibleControl(playerControlContainer, hadFocus) {
        if (!playerControlContainer || !hadFocus) {
            return;
        }
        const nextControl = [...playerControlContainer.querySelectorAll('button')]
            .find((control) => getComputedStyle(control).display !== 'none');
        if (nextControl) {
            nextControl.focus();
        }
    }

    // Playback starts here rather than through the autoplay attribute, so the
    // markup offers the pause control as the only way the video runs on its own.
    startVideo(video, playerControlContainer) {
        const playAttempt = video.play();
        if (!playAttempt || typeof playAttempt.then !== 'function') {
            this.setPlayedState(playerControlContainer, !video.paused);
            return Promise.resolve(!video.paused);
        }
        return playAttempt.then(() => {
            this.setPlayedState(playerControlContainer, true);
            return true;
        }, () => {
            this.setPlayedState(playerControlContainer, false);
            return false;
        });
    }

    async connectedCallback() {
        await childrenLoaded(this);
        this.querySelectorAll('.home-page-hero__video-control-container').forEach((controls) => {
            controls.dataset.ready = 'true';
        });
        const iframe = this.querySelector('iframe');
        const video = this.querySelector('video');
        const videoContainer = this.querySelector('.home-page-hero__image');
        const playerControlContainer = videoContainer.querySelector('.home-page-hero__player-container');

        if (video) {
            video.addEventListener('play', () => this.setPlayedState(playerControlContainer, true));
            video.addEventListener('pause', () => this.setPlayedState(playerControlContainer, false));
            this.startVideo(video, playerControlContainer);
        }

        if (iframe) {
            const videoControl = this.querySelector('.home-page-hero__video-control');
            const controlledId = videoControl ? videoControl.getAttribute('aria-controls') : null;
            if (iframe.id) {
                this.querySelectorAll('.home-page-hero__video-control').forEach((control) => {
                    control.setAttribute('aria-controls', iframe.id);
                });
            } else if (controlledId) {
                iframe.id = controlledId;
            }
        }

        if (this.getAttribute('data-is-preview') === "1") {
            return
        }

        if (iframe) {
            const videoId = new URL(videoContainer.getAttribute('data-src')).searchParams.get('v');
            const url = this.setUrlParams(videoContainer.getAttribute('src'), videoId);
            iframe.setAttribute('src', url.toString());
            iframe.setAttribute('allow', 'autoplay');
        }

        if (!video && !this.isMobile()) {
            this.setPlayedState(playerControlContainer);
        }

        const that = this;
        this.querySelectorAll('.home-page-hero__video-control').forEach(function (videoControl) {
            videoControl.addEventListener('click', function (e) {
                e.stopPropagation()
                const hadFocus = document.activeElement === videoControl;
                const iframe = videoContainer.querySelector('iframe');
                const video = videoContainer.querySelector('video');

                if (videoControl.matches('button.play-button')) {
                    if (iframe && iframe.src.includes('vimeo')) {
                        new Player(iframe).play();
                    } else if (iframe && iframe.src.includes('youtube')) {
                        iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                    }

                    // A local video only counts as playing once play() succeeds; a refusal
                    // keeps the Play button and says so instead of claiming playback.
                    if (video) {
                        that.startVideo(video, playerControlContainer).then((started) => {
                            that.moveFocusToVisibleControl(playerControlContainer, hadFocus);
                            that.announceState(playerControlContainer,
                                started ? 'Background video playing.' : 'Background video could not play.');
                        });
                    } else {
                        that.setPlayedState(playerControlContainer, true);
                        that.moveFocusToVisibleControl(playerControlContainer, hadFocus);
                        that.announceState(playerControlContainer, 'Background video playing.');
                    }
                }

                if (videoControl.matches('button.pause-button')) {
                    if (iframe && iframe.src.includes('vimeo')) {
                        new Player(iframe).pause();
                    } else if (iframe && iframe.src.includes('youtube')) {
                        iframe.contentWindow.postMessage('{"event":"command","func":"stopVideo","args":""}', '*');
                    }

                    if (video) {
                        video.pause();
                    }

                    that.setPlayedState(playerControlContainer, false);
                    that.moveFocusToVisibleControl(playerControlContainer, hadFocus);
                    that.announceState(playerControlContainer, 'Background video paused.');
                }
            });
        });
    }
}

customElements.define('hero-banner', HeroVideo, {extends: 'section'});
